import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Edit, Trash2, X, Save, MinusCircle, PlusCircle, Cog, Search, CheckCircle2, Users } from "lucide-react";
import ErrorNotification from "../../ErrorNotification";

const OemEpNoTable = ({ epNoData, accessToken, setError, setRefresh, refresh, setToast }) => {
  const [isEditingAccounts, setIsEditingAccounts] = useState(false);
  const [isEditingUsers, setIsEditingUsers] = useState(false);
  const [selectedEpNo, setSelectedEpNo] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [accountsToRemove, setAccountsToRemove] = useState([]);
  const [accountsToAdd, setAccountsToAdd] = useState([]);
  const [usersToUpdate, setUsersToUpdate] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [recordToDeleteEpNo, setRecordToDeleteEpNo] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState({ EpNo: "", Oem: "" });
  const [error, setLocalError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const displayData = showAll ? epNoData : epNoData.slice(0, 4);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (selectedEpNo) {
      fetchLinkedAccounts(selectedEpNo);
      fetchAvailableAccounts();
      fetchAllUsers();
    }
  }, [selectedEpNo]);

  const fetchLinkedAccounts = async (epNo) => {
    setIsLoading(true);
    try {
      const query = `
        query MyQuery($epNo: String!) {
          getAvailableCostAccounts(EpNo: $epNo) {
            id
            AccountName
          }
        }
      `;

      const variables = {
        epNo: epNo,
      };

      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (
        response.data.errors &&
        response.data.errors[0].errorType === "Unauthorized"
      ) {
        setError("Unauthorized");
      } else if (response.data.data && response.data.data.getAvailableCostAccounts) {
        const accounts = response.data.data.getAvailableCostAccounts;
        setLinkedAccounts(accounts);
      }
    } catch (error) {
      console.error("Error fetching linked accounts:", error);
      setToast({
        type: "error",
        message: "Failed to fetch linked accounts",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setIsLoading(true);
    try {
      const allUsersQuery = `
        query GetAllUsers {
          getAllUsersForBudgetAlert {
            emails
          }
        }
      `;

      const response = await axios.post(
        apiUrl,
        { query: allUsersQuery },
        { headers: { Authorization: accessToken } }
      );

      if (response.data.data?.getAllUsersForBudgetAlert?.emails) {
        setAvailableUsers(response.data.data.getAllUsersForBudgetAlert.emails);
      }
    } catch (error) {
      console.error("Error fetching all users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableAccounts = async () => {
    setIsLoading(true);
    try {
      const query = `
        query MyQuery($epNo: String!) {
          getAvailableCostAccounts(EpNo: $epNo) {
            id
            AccountName
          }
        }
      `;

      const variables = {
        epNo: "default",
      };

      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (
        response.data.errors &&
        response.data.errors[0].errorType === "Unauthorized"
      ) {
        setError("Unauthorized");
      } else if (response.data.data && response.data.data.getAvailableCostAccounts) {
        const accounts = response.data.data.getAvailableCostAccounts;
        setAvailableAccounts(accounts);
      }
    } catch (error) {
      console.error("Error fetching available accounts:", error);
      setToast({
        type: "error",
        message: "Failed to fetch available accounts",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRow({ ...newRow, [name]: value });
  };

  const handleSubmit = async () => {
    const query = `
      mutation MyMutation($EpNo: String!, $Oem: String!) {
        addEpNoandOem(EpNo: $EpNo, Oem: $Oem)
      }
    `;

    const variables = {
      EpNo: newRow.EpNo,
      Oem: newRow.Oem,
    };

    await axios
      .post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if (
          response.data.errors &&
          response.data.errors[0].errorType === "Unauthorized"
        )
          setLocalError(
            "Edit access is not granted to your account for this resource."
          );
        else {
          setToast({
            type: "success",
            message: "EP No added successfully",
          });
        }
      })
      .catch((error) => {
        console.log(error);
        setToast({
          type: "error",
          message: "Failed to add EP No",
        });
      });
    setRefresh(!refresh);
  };

  const handleAddRow = (e) => {
    e.preventDefault();
    handleSubmit();
    setNewRow({ EpNo: "", Oem: "" });
    setIsAdding(false);
  };

  const handleEditAccounts = (item) => {
    setSelectedEpNo(item.EpNo);
    setIsEditingAccounts(true);
    setAccountsToRemove([]);
    setAccountsToAdd([]);
  };

  const handleEditUsers = (item) => {
    setSelectedEpNo(item.EpNo);
    setIsEditingUsers(true);
    setLinkedUsers(item.Users || []);
    setUsersToUpdate(item.Users || []);
    setUserSearchTerm("");
  };

  const handleRemoveAccount = (account) => {
    setAccountsToRemove((prev) => {
      if (prev.some((acc) => acc.id === account.id)) {
        return prev.filter((acc) => acc.id !== account.id);
      }
      return [...prev, account];
    });
  };

  const handleAddAccount = (account) => {
    setAccountsToAdd((prev) => {
      if (prev.some((acc) => acc.id === account.id)) {
        return prev.filter((acc) => acc.id !== account.id);
      }
      return [...prev, account];
    });
  };

  const handleUserToggle = (email) => {
    setUsersToUpdate(prev => {
      if (prev.includes(email)) {
        return prev.filter(u => u !== email);
      }
      return [...prev, email];
    });
  };

  const handleSubmitAccountChanges = async () => {
    setIsSubmitting(true);

    try {
      let updatedAccounts = 0;

      if (accountsToRemove.length > 0) {
        const query = `
          mutation MyMutation($accountNames: [String!]!) {
            updateAccountsEpNo(accountNames: $accountNames, EpNo: "default") {
              success
              message
              updatedAccounts
              failedAccounts
            }
          }
        `;

        const variables = {
          accountNames: accountsToRemove.map((account) => account.AccountName),
        };

        const response = await axios.post(
          apiUrl,
          { query, variables },
          { headers: { Authorization: accessToken } }
        );

        if (response.data.data?.updateAccountsEpNo?.updatedAccounts) {
          updatedAccounts += response.data.data.updateAccountsEpNo.updatedAccounts;
        }

        if (response.data.errors) {
          throw new Error(response.data.errors[0]?.message || "Failed to update accounts");
        }
      }

      if (accountsToAdd.length > 0) {
        const query = `
          mutation MyMutation($accountNames: [String!]!, $EpNo: String!) {
            updateAccountsEpNo(accountNames: $accountNames, EpNo: $EpNo) {
              success
              message
              updatedAccounts
              failedAccounts
            }
          }
        `;

        const variables = {
          accountNames: accountsToAdd.map((account) => account.AccountName),
          EpNo: selectedEpNo,
        };

        const response = await axios.post(
          apiUrl,
          { query, variables },
          { headers: { Authorization: accessToken } }
        );

        if (response.data.data?.updateAccountsEpNo?.updatedAccounts) {
          updatedAccounts += response.data.data.updateAccountsEpNo.updatedAccounts;
        }

        if (response.data.errors) {
          throw new Error(response.data.errors[0]?.message || "Failed to update accounts");
        }
      }

      setRefresh(!refresh);
      setIsEditingAccounts(false);
      setSelectedEpNo(null);
      setAccountsToRemove([]);
      setAccountsToAdd([]);

      setToast({
        type: "success",
        message: `Successfully updated accounts`,
      });
    } catch (error) {
      console.error("Error updating accounts:", error);
      setToast({
        type: "error",
        message: "Failed to update accounts",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitUserChanges = async () => {
    setIsSubmitting(true);
    try {
      const currentEp = epNoData.find(item => item.EpNo === selectedEpNo);
      if (!currentEp) return;

      const userMutation = `
        mutation UpdateEpNoUsers($id: ID!, $Users: [String]!) {
          updateEpNoUsers(id: $id, Users: $Users) {
            id
            Users
          }
        }
      `;

      const response = await axios.post(
        apiUrl,
        {
          query: userMutation,
          variables: { id: currentEp.id, Users: usersToUpdate }
        },
        { headers: { Authorization: accessToken } }
      );

      if (response.data.errors) {
        throw new Error(response.data.errors[0]?.message || "Failed to update users");
      }

      setRefresh(!refresh);
      setIsEditingUsers(false);
      setSelectedEpNo(null);
      setUsersToUpdate([]);
      setUserSearchTerm("");

      setToast({
        type: "success",
        message: "Successfully updated associated users",
      });
    } catch (error) {
      console.error("Error updating users:", error);
      setToast({
        type: "error",
        message: "Failed to update user associations",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirmation = (id, epNo) => {
    setRecordToDelete(id);
    setRecordToDeleteEpNo(epNo);
    setOpenDialog(true);
  };

  const deleteRecord = async () => {
    setIsDeleting(true);
    try {
      const epNoToDelete = epNoData.find((item) => item.id === recordToDelete)?.EpNo;

      if (!epNoToDelete) {
        console.error("Could not find EP No for deletion");
        setToast({
          type: "error",
          message: "Could not find EP No for deletion",
        });
        return;
      }

      const query = `
        mutation DeleteEpNo($EpNoIdentifier: String, $EpNo: String) {
          deleteEpNo(EpNo: $EpNo, EpNoIdentifier: $EpNoIdentifier) {
            success
            message
            updatedAccounts
            failedAccounts
            epNoDeleted
          }
        }
      `;

      const variables = {
        EpNoIdentifier: recordToDelete,
        EpNo: epNoToDelete,
      };

      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.data.errors) {
        throw new Error(
          response.data.errors[0]?.message || "Failed to delete EP No"
        );
      }

      const result = response.data.data?.deleteEpNo;

      if (result?.success) {
        setToast({
          type: "success",
          message: result.message,
        });
      } else {
        throw new Error(result?.message || "Failed to delete EP No");
      }

      setOpenDialog(false);
      setRecordToDelete(null);
      setRecordToDeleteEpNo(null);
      setRefresh(!refresh);
    } catch (error) {
      console.error("Error deleting EP No:", error);
      setToast({
        type: "error",
        message: error.message || "Failed to delete EP No",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        {error && (
          <ErrorNotification message={error} onClose={() => setLocalError(null)} />
        )}
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-[#003366]">Ep No</h3>
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add New EP No
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#003366]/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    EP No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    OEM
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isAdding && (
                  <tr className="bg-[#003366]/5">
                    <td className="px-6 py-4">
                      <input
                        required
                        type="text"
                        name="EpNo"
                        value={newRow.EpNo}
                        onChange={handleInputChange}
                        placeholder="Enter EP No"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366]"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        required
                        type="text"
                        name="Oem"
                        value={newRow.Oem}
                        onChange={handleInputChange}
                        placeholder="Enter OEM"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center space-x-2">
                      <button
                        onClick={handleAddRow}
                        disabled={
                          newRow.EpNo === "" || newRow.Oem === ""
                        }
                        className={
                          newRow.EpNo === "" || newRow.Oem === ""
                            ? "text-gray-600"
                            : `text-[#003366] hover:text-[#004488] font-medium`
                        }
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setNewRow({ EpNo: "", Oem: "" });
                          setIsAdding(false);
                        }}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}
                {displayData.map((item, i) => (
                  <tr key={i} className="hover:bg-[#003366]/5">
                    <td className="px-6 py-4 whitespace-nowrap text-[#003366]">
                      {item.EpNo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {item.Oem}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center space-x-2">
                      <button
                        onClick={() => handleEditUsers(item)}
                        className="text-[#003366] hover:text-[#004488] p-1 rounded-full"
                        title="Associate Users"
                      >
                        <Users className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEditAccounts(item)}
                        className="text-[#003366] hover:text-[#004488] p-1 rounded-full"
                        title="Modify Accounts"
                      >
                        <Cog className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteConfirmation(item.id, item.EpNo)
                        }
                        className="text-red-600 hover:text-red-800 p-1 rounded-full"
                        title="Delete"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            visible: true,
                            x: rect.left + rect.width / 2,
                            y: rect.bottom + 5,
                          });
                        }}
                        onMouseLeave={() =>
                          setTooltip({ visible: false, x: 0, y: 0 })
                        }
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {epNoData.length > 4 && (
              <div className="text-center mt-4">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-[#003366] hover:text-[#004488] font-medium"
                >
                  {showAll ? "Show Less" : "Show More"}
                </button>
              </div>
            )}
            {epNoData.length === 0 && !isAdding && (
              <div className="text-center py-12">
                <div className="text-[#003366]">
                  <svg
                    className="w-12 h-12 mx-auto mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 12H4"
                    />
                  </svg>
                  <p className="text-lg font-semibold">No Data Available</p>
                  <p className="mt-2">
                    Click the Add New button to add your first item
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
          className="bg-black text-white text-sm rounded px-2 py-1"
        >
          Delete
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: "8px",
            maxWidth: "450px",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          style={{
            padding: "16px 24px",
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#003366",
          }}
        >
          {"Confirm Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{
              color: "#4a4a4a",
              fontSize: "1rem",
              marginBottom: "8px",
            }}
          >
            Are you sure you want to delete the EP No{" "}
            <b>{recordToDeleteEpNo}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
          <button
            onClick={() => setOpenDialog(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#FFD700",
              color: "#003366",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFE44D")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFD700")
            }
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={deleteRecord}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#003366",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#004488")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#003366")
            }
            autoFocus
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Save size={16} />
            )}
            Confirm
          </button>
        </DialogActions>
      </Dialog>

      {/* Edit Accounts Modal */}
      {isEditingAccounts && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-xl bg-white rounded-lg shadow-xl overflow-y-auto">
           <div className="flex flex-col max-h-[90vh]">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-[#003366]">
                  Manage Accounts for EP No: {selectedEpNo}
                </h2>
                <button
                  onClick={() => setIsEditingAccounts(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <CircularProgress style={{ color: "#003366" }} />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Accounts linked to the EpNo section */}
                  <div>
                    <h3 className="text-lg font-medium text-[#003366] mb-2">
                      Accounts linked to this EP No
                    </h3>
                    <div className="border border-gray-300 rounded-md p-2 max-h-48 overflow-y-auto">
                      {linkedAccounts.length === 0 ? (
                        <p className="text-sm text-gray-500 p-2">No accounts currently linked to this EP No</p>
                      ) : (
                        linkedAccounts.map((account) => (
                          <div
                            key={account.id}
                            className={`flex items-center justify-between py-2 px-3 hover:bg-gray-50 ${accountsToRemove.some(acc => acc.id === account.id)
                              ? "bg-[#003366]/10"
                              : ""
                              }`}
                          >
                            <span className="text-sm">
                              {account.AccountName}
                            </span>
                            <button
                              onClick={() => handleRemoveAccount(account)}
                              className={`p-1 rounded-full ${accountsToRemove.some(acc => acc.id === account.id)
                                ? "text-[#003366] hover:text-[#002244] bg-[#003366]/10"
                                : "text-gray-500 hover:text-[#003366]"
                                }`}
                              title="Remove account from this EP No"
                            >
                              <MinusCircle className="w-5 h-5" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                      {accountsToRemove.length > 0 && (
                        <p className="text-sm text-[#003366] mt-1">
                          {accountsToRemove.length} account(s) will be reset to default
                        </p>
                      )}
                  </div>

                  {/* Add accounts section */}
                  <div>
                    <h3 className="text-lg font-medium text-[#003366] mb-2">
                      Add accounts
                    </h3>
                    <div className="border border-gray-300 rounded-md p-2 max-h-48 overflow-y-auto">
                      {availableAccounts.length === 0 ? (
                        <p className="text-sm text-gray-500 p-2">No accounts available to add</p>
                      ) : (
                        availableAccounts.map((account) => (
                          <div
                            key={account.id}
                            className={`flex items-center justify-between py-2 px-3 hover:bg-gray-50 ${accountsToAdd.some(acc => acc.id === account.id)
                              ? "bg-[#003366]/10"
                              : ""
                              }`}
                          >
                            <span className="text-sm">
                              {account.AccountName}
                            </span>
                            <button
                              onClick={() => handleAddAccount(account)}
                              className={`p-1 rounded-full ${accountsToAdd.some(acc => acc.id === account.id)
                                ? "text-[#003366] hover:text-[#002244] bg-[#003366]/10"
                                : "text-gray-500 hover:text-[#003366]"
                                }`}
                              title="Add account to this EP No"
                            >
                              <PlusCircle className="w-5 h-5" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                      {accountsToAdd.length > 0 && (
                        <p className="text-sm text-[#003366] mt-1">
                          {accountsToAdd.length} account(s) will be added to this EP No
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t">
              <button
                type="button"
                onClick={() => setIsEditingAccounts(false)}
                className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#003366] rounded hover:bg-[#FFD700]/80"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSubmitAccountChanges}
                className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
                disabled={isSubmitting || (accountsToAdd.length === 0 && accountsToRemove.length === 0)}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    

      {/* Edit Users Modal */}
      {isEditingUsers && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 overflow-hidden z-[1000]">
          <div className="w-full max-w-xl bg-white rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-[#003366]">
                  Associate Users for EP No: {selectedEpNo}
                </h2>
                <button
                  onClick={() => setIsEditingUsers(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <CircularProgress style={{ color: "#003366" }} />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Search Section */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Search Users</label>
                    <input
                      type="text"
                      placeholder="Search by email..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#003366]"
                    />
                  </div>

                  {/* User List */}
                  <div>
                    <h3 className="text-lg font-medium text-[#003366] mb-2">
                      Available Users
                    </h3>
                    <div className="border border-gray-300 rounded-md p-2 max-h-64 overflow-y-auto">
                      {availableUsers
                        .filter(email => email.toLowerCase().includes(userSearchTerm.toLowerCase()))
                        .map((email) => {
                          const isSelected = usersToUpdate.includes(email);
                          const wasOriginallyLinked = (linkedUsers || []).includes(email);

                          return (
                            <div
                              key={email}
                              onClick={() => handleUserToggle(email)}
                              className={`flex items-center justify-between py-2 px-3 hover:bg-gray-50 cursor-pointer ${isSelected
                                ? "bg-[#003366]/10"
                                : ""
                                }`}
                            >
                              <div className="flex flex-col">
                                <span className={`text-sm ${wasOriginallyLinked && !isSelected ? "line-through text-gray-400" : ""}`}>
                                  {email}
                                </span>
                                {wasOriginallyLinked && isSelected && <span className="text-[10px] text-[#003366] font-bold">STAYING</span>}
                                {wasOriginallyLinked && !isSelected && <span className="text-[10px] text-red-500 font-bold">REMOVING</span>}
                                {!wasOriginallyLinked && isSelected && <span className="text-[10px] text-green-600 font-bold">ADDING</span>}
                              </div>
                              <div className={`p-1 rounded-full ${isSelected ? "text-[#003366]" : "text-gray-400"}`}>
                                {isSelected ? <PlusCircle className="w-5 h-5 rotate-45" /> : <PlusCircle className="w-5 h-5" />}
                              </div>
                            </div>
                          );
                        })
                      }
                      {availableUsers.filter(email => email.toLowerCase().includes(userSearchTerm.toLowerCase())).length === 0 && (
                        <p className="text-sm text-gray-500 p-2 text-center">No users found</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t mt-auto">
              <button
                type="button"
                onClick={() => setIsEditingUsers(false)}
                className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#003366] rounded hover:bg-[#FFD700]/80"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSubmitUserChanges}
                className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
                disabled={isSubmitting || JSON.stringify(usersToUpdate.sort()) === JSON.stringify((linkedUsers || []).sort())}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OemEpNoTable;